import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Globals } from 'app/_globals';
import { ReferenceTurbine } from 'app/_models/turbine.model';
import { CompetencyService } from 'app/_services/competency.service';
import { CompetencyTypesService } from 'app/_services/competency-types.service';
import { NotificationService } from 'app/_services/notification.service';
import { canManageCompetencyForSseRoute, hasSseCompetencyRestrictions } from 'app/_utils/competency-permissions.utils';
import DateTimeUtils from 'app/_utils/datetime.utils';
import { environment } from 'environments/environment';

@Component({
  selector: 'app-add-competency-form',
  templateUrl: './add-competency-form.component.html',
})
export class AddCompetencyFormComponent implements OnInit, OnChanges {
  @Input() skylarkUserId: number;
  @Input() siteOwnerId: number;
  @Input() flowMode: 'standard' | 'invite' = 'standard';
  @Input() approvalMode: 'privileged' | 'limited';

  @Output() competencyAdded = new EventEmitter<void>();
  @Output() competencyCollected = new EventEmitter<{
    competencyType: any;
    manufacturer: string;
    models: string[];
    ratings: string[];
    document: File;
    expiryDate: Date;
    preApprovedSelected: boolean;
    requestEvidence: boolean;
    submitForApprovalSelected: boolean;
    applyToAll: boolean;
  }>();

  public competencyTypes: any[] = [];
  public competencyTypeOptions: any[] = [];
  public selectedCompetencyType: any = null;

  public manufacturers: string[] = [];
  public manufacturerOptions: any[] = [];
  public selectedManufacturer: string = null;

  public turbineSearchResults: ReferenceTurbine[] = [];
  public turbineModels: string[] = [];
  public modelOptions: any[] = [];
  public selectedModels: string[] = [];

  public turbineRatings: string[] = [];
  public ratingOptions: any[] = [];
  public selectedRatings: string[] = [];

  public selectedDocument: File = null;
  public expiryDate: Date = null;
  public preApprovedSelected = false;
  public requestEvidence = false;
  public submitForApprovalSelected = false;
  public limitedAction: 'request' | 'submit' = 'request';
  public applyToAll = false;
  public isSavingCompetency = false;
  public showTurbineFields = false;

  public today: Date = new Date();
  private currentYear = new Date().getFullYear();
  public datepickerYearRange = this.currentYear + ':' + (this.currentYear + 10);

  constructor(
    private globals: Globals,
    private competencyService: CompetencyService,
    private competencyTypesService: CompetencyTypesService,
    private notificationService: NotificationService,
  ) {}

  ngOnInit() {
    if (this.siteOwnerId || this.isInviteFlow) {
      this.loadCompetencyTypes();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.siteOwnerId && !changes.siteOwnerId.firstChange && changes.siteOwnerId.currentValue) {
      this.loadCompetencyTypes();
      this.resetAddForm();
    }
  }

  private loadCompetencyTypes(): void {
    this.competencyTypesService.getAllCompetencyTypesByTypeFilteredForSiteOwner('WORKER', this.siteOwnerId)
      .subscribe((types) => {
        this.competencyTypes = this.filterCompetencyTypesForCurrentRoute(types);
        this.competencyTypeOptions = this.competencyTypes.map(competencyType => {
          return ({
            label: competencyType.shortName ? `${competencyType.shortName} - ${competencyType.value}` : competencyType.value,
            value: competencyType
          });
        });
      });
  }

  public onCompetencyTypeChange(type): void {
    this.showTurbineFields = (type.type === 'TURBINE');
    if (this.showTurbineFields) {
      this.competencyService.getTurbineManufacturers()
        .subscribe((manufacturers) => {
          this.manufacturers = manufacturers;
          this.manufacturerOptions = [{ label: 'All', value: null }].concat(
            manufacturers.map(m => ({ label: m, value: m }))
          );
        });
    }
    this.resetTurbineFields();
  }

  private resetTurbineFields(): void {
    this.selectedManufacturer = null;
    this.manufacturerOptions = [];
    this.turbineSearchResults = [];
    this.turbineModels = [];
    this.modelOptions = [];
    this.selectedModels = [];
    this.turbineRatings = [];
    this.ratingOptions = [];
    this.selectedRatings = [];
  }

  public onManufacturerChange(manufacturer: string): void {
    this.competencyService.searchTurbines({ manufacturer: manufacturer })
      .subscribe((results) => {
        this.turbineSearchResults = results;
        this.turbineModels = Array.from(new Set(results.map(r => r.model)));
        this.modelOptions = this.turbineModels.map(m => ({ label: m, value: m }));
        this.turbineRatings = [];
        this.ratingOptions = [];
        this.selectedModels = [];
        this.selectedRatings = [];
      });
  }

  public onModelsChange(models: string[]): void {
    if (!models || !models.length) {
      this.turbineRatings = [];
      this.ratingOptions = [];
      this.selectedRatings = [];
      return;
    }
    this.turbineRatings = Array.from(new Set(
      this.turbineSearchResults
        .filter(r => models.indexOf(r.model) !== -1)
        .map(r => r.rating)
        .filter(r => !!r)
    ));
    this.ratingOptions = this.turbineRatings.map(r => ({ label: r, value: r }));
    this.selectedRatings = this.selectedRatings.filter(r => this.turbineRatings.indexOf(r) !== -1);
  }

  public onFileSelected(event: any): void {
    this.selectedDocument = event.target.files[0] || null;
  }

  public onPreApprovedToggle(): void {
    this.preApprovedSelected = !this.preApprovedSelected;
    if (this.preApprovedSelected) {
      this.requestEvidence = false;
      this.submitForApprovalSelected = false;
    } else if (!this.submitForApprovalSelected) {
      this.expiryDate = null;
      this.selectedDocument = null;
    }
  }

  public onRequestEvidenceToggle(): void {
    if (this.isForcedRequestEvidenceFlow) {
      return;
    }

    this.requestEvidence = !this.requestEvidence;
    if (this.requestEvidence) {
      this.preApprovedSelected = false;
      this.submitForApprovalSelected = false;
      this.expiryDate = null;
      this.selectedDocument = null;
    }
  }

  public onLimitedActionChange(action: 'request' | 'submit'): void {
    this.limitedAction = action;
    this.submitForApprovalSelected = action === 'submit';
    this.requestEvidence = false;
    this.preApprovedSelected = false;
    this.expiryDate = null;

    if (action !== 'submit') {
      this.selectedDocument = null;
    }
  }

  public addCompetency(): void {
    const requestEvidence = this.shouldRequestEvidenceOnSubmit;

    if (this.isInviteFlow) {
      this.competencyCollected.emit({
        competencyType: this.selectedCompetencyType,
        manufacturer: this.selectedManufacturer || '',
        models: this.selectedModels || [],
        ratings: this.selectedRatings || [],
        document: this.selectedDocument,
        expiryDate: this.expiryDate,
        preApprovedSelected: this.preApprovedSelected,
        requestEvidence: requestEvidence,
        submitForApprovalSelected: this.submitForApprovalSelected,
        applyToAll: this.applyToAll,
      });
      this.resetAddForm();
      return;
    }

    this.isSavingCompetency = true;
    const payload = {
      skylarkUserId: this.skylarkUserId,
      competency_type: this.selectedCompetencyType.shortName || this.selectedCompetencyType.value,
      manufacturer: this.selectedManufacturer || '',
      models: this.selectedModels || [],
      ratings: this.selectedRatings || [],
      notes: '',
      expiry_date: this.formatDate(this.expiryDate),
      created_by: this.globals.user.login,
      last_updated_by: this.globals.user.login,
      site_owner_id: this.siteOwnerId,
      pre_approved: this.preApprovedSelected,
      request_evidence: requestEvidence,
    };

    this.competencyService.addCompetencyToUser(payload, this.selectedDocument).subscribe(
      () => {
        this.isSavingCompetency = false;
        this.notificationService.notify('success', this.submitForApprovalSelected ? 'Competency submitted' : 'Competency saved');
        this.competencyAdded.emit();
        this.resetAddForm();
      },
      (error) => {
        this.isSavingCompetency = false;
        this.notificationService.notify('danger', this.competencyService.getCompetencySubmitError(error, 'Error adding competency'));
      }
    );
  }

  public resetAddForm(): void {
    this.selectedCompetencyType = null;
    this.showTurbineFields = false;
    this.resetTurbineFields();
    this.selectedDocument = null;
    this.expiryDate = null;
    this.preApprovedSelected = false;
    this.requestEvidence = false;
    this.submitForApprovalSelected = false;
    this.limitedAction = 'request';
    this.applyToAll = false;
  }

  public get isInviteFlow(): boolean {
    return this.flowMode === 'invite';
  }

  public get isPrivilegedFlow(): boolean {
    return this.approvalMode === 'privileged';
  }

  public get isSseRestrictedContext(): boolean {
    return hasSseCompetencyRestrictions(this.globals.user, this.siteOwnerId);
  }

  public get isForcedRequestEvidenceFlow(): boolean {
    return this.isPrivilegedFlow && this.siteOwnerId === environment.WFSO_OWNER_SKYLARK_ID;
  }

  public get forcedRequestEvidenceHelperText(): string {
    return this.preApprovedSelected
      ? 'Competency will be pre-approved.'
      : 'Evidence will be requested automatically.';
  }

  public get showRequestEvidenceToggle(): boolean {
    return this.isPrivilegedFlow
      && !this.isForcedRequestEvidenceFlow
      && !this.preApprovedSelected
      && !this.submitForApprovalSelected;
  }

  public get showApplyToAllOption(): boolean {
    return this.isInviteFlow;
  }

  public get showDocumentSection(): boolean {
    return this.preApprovedSelected || this.submitForApprovalSelected;
  }

  public get submitButtonLabel(): string {
    if (this.submitForApprovalSelected) {
      return 'Submit for Approval';
    }

    if (!this.isPrivilegedFlow) {
      return 'Request Document';
    }

    return 'Add';
  }

  public get isAddDisabled(): boolean {
    return !this.selectedCompetencyType
      || (this.preApprovedSelected && !this.expiryDate)
      || (this.submitForApprovalSelected && !this.expiryDate)
      || (this.expiryDate && this.isExpiryDateInPast)
      || (this.isForcedRequestEvidenceFlow && this.preApprovedSelected && !this.selectedDocument)
      || (this.submitForApprovalSelected && !this.selectedDocument)
      || this.isSavingCompetency;
  }

  public get isExpiryDateInPast(): boolean {
    if (!this.expiryDate) {
      return false;
    }

    return DateTimeUtils.isDateExpired(this.expiryDate);
  }

  public get shouldRequestEvidenceOnSubmit(): boolean {
    if (this.preApprovedSelected || this.submitForApprovalSelected) {
      return false;
    }

    if (this.isForcedRequestEvidenceFlow) {
      return true;
    }

    if (this.requestEvidence) {
      return true;
    }

    if (!this.isPrivilegedFlow) {
      // Contractor Manager and has not chosen 'submit for approval'
      return true;
    }

    return false;
  }

  private formatDate(date: Date): string {
    if (!date) { return ''; }
    return DateTimeUtils.localDateToTimelessString(date);
  }

  private filterCompetencyTypesForCurrentRoute(types: any[]): any[] {
    if (!this.isSseRestrictedContext) {
      return types;
    }

    return types.filter((competencyType) => canManageCompetencyForSseRoute(
      competencyType,
      this.globals.user.canEditTurbineCompetencies,
      this.globals.user.canEditNonTurbineCompetencies,
    ));
  }
}
